'use client'

import { useMemo } from 'react'

import { JobPageItem, TermsConditions } from '@types'

import { match } from 'ts-pattern'

import { AppcastDisclaimer, GenericDisclaimer } from '@components/job/apply-disclaimer/disclaimers'

import useLocale from '@hooks/client/useLocale'
import Link from 'next/link'
import styles from './apply-disclaimer.module.scss'

export type ApplyDisclaimerProps = {
  termsConditions: TermsConditions[]
  jobSource: JobPageItem['source']
}

function ApplyDisclaimer({ termsConditions, jobSource }: ApplyDisclaimerProps): JSX.Element {
  const showDisclaimer = useMemo(() => termsConditions.length > 0, [termsConditions])
  const country = useLocale()

  const { sourceList, components }: { sourceList: string; components: JSX.Element[] } = useMemo(() => {
    let sl = ''
    const ct = []
    for (let i = 0; i < termsConditions.length; i += 1) {
      const termsCondition = termsConditions[i]
      sl += termsConditions[i].provider
      ct.push(<span key={`prov_${i}`}>{`${termsCondition.provider}'s `}</span>)
      for (let j = 0; j < termsCondition.documents.length; j += 1) {
        const doc = termsCondition.documents[j]
        ct.push(
          <a key={`doc_${i}_${j}`} className={styles.link} href={doc.url} target="_blank" rel="noopener noreferrer">
            {doc.title}
          </a>,
        )
        if (j === termsCondition.documents.length - 2) {
          ct.push(<span key={`and_${i}_${j}`}> and </span>)
        } else if (j <= termsCondition.documents.length - 3) {
          ct.push(<span key={`and_${i}_${j}`}>/</span>)
        }
      }
      if (i === termsConditions.length - 2) {
        sl += ' and '
        ct.push(<span key={`and_${i}`}> and </span>)
      } else if (i <= termsConditions.length - 3) {
        ct.push(<span key={`and_${i}`}>, </span>)
        sl += ', '
      }
    }
    return { sourceList: sl, components: ct }
  }, [termsConditions])

  if (showDisclaimer) {
    const disclaimer = match(jobSource)
      .with('APPCAST_EASY_APPLY', () => <AppcastDisclaimer className={styles.custom} />)
      .with('', () => <></>)
      .otherwise(() => (
        <GenericDisclaimer source={sourceList} className={styles.generic}>
          {components}
        </GenericDisclaimer>
      ))

    return (
      <>
        {disclaimer}
        <p className={styles.additional}>
          SonicJobs&apos;{' '}
          <Link target="_blank" href={`/${country}/terms-conditions`}>
            Terms & Conditions
          </Link>{' '}
          and{' '}
          <Link target="_blank" href={`/${country}/privacy`}>
            Privacy Policy
          </Link>{' '}
          also apply.
        </p>
      </>
    )
  }
  return <></>
}

export default ApplyDisclaimer
